import React, { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DataTable, { IDataTableHandle } from '@srm/ui/data-table';
import { IDataTableSource, IServerLoadOptions } from '@srm/ui/data-table/server';
import { AddressBookService } from '@services/AddressBookService';
import { IAbookTab } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookSettings } from '../IAbookSettings';
import { IAbookpickerRefActions } from '@atoms/Abookpicker/IAbookpickerRefActions';
import { ConfigService } from '@/configuration/services/configService';
import { sendNotification } from '@srm/ui/lib/notifications';
import { errorText, errorTraceId } from '@srm/core/errors';
import { buildAbookColumns } from './abookGridColumns';

export interface ITreeModeControlProp extends Omit<IAbookSettings, 'showChips'> {
    currentTab: IAbookTab;
    onSelectedRowItems: (value: IAddressBookData[]) => void;
    onConfirmRow?: (row: IAddressBookData) => void;
    onSelectedKeys: (value: number[]) => void;
    getParams: (options: unknown) => string;
    controlRef: React.Ref<IAbookpickerRefActions>;
}

/** Корень дерева адресной книги на бэкенде. */
const ROOT_NODE = '0';

/**
 * Дерево адресной книги (подразделения и группы) на базовом гриде.
 *
 * Узлы ленивые: корни приходят серверным источником, дети — запросом по `node`
 * при раскрытии. Строку дерева адресует `id` (узел), а выбранное значение поля —
 * `key` сотрудника, поэтому стартовый выбор ищется по ключу среди загруженных
 * узлов. Выбирать можно только те узлы, которые бэкенд помечает `canBeSelected`.
 */
const TreeModeControl: FC<ITreeModeControlProp> = (p: ITreeModeControlProp) => {
    const config = ConfigService.get();
    const applyFilterOnClick = config.application.applyGridFilterOnClick ?? false;
    const tableRef = useRef<IDataTableHandle<IAddressBookData>>(null);
    // Настройки приходят новым объектом на каждый рендер модалки, а загрузчик
    // таблицы обязан быть стабильным: его смена — другая выборка (с начала, выбор снят)
    const settingsRef = useRef(p);
    settingsRef.current = p;
    const [reloadToken, setReloadToken] = useState(0);
    /** Загруженные ветки: ключ узла → его дети. */
    const [childRows, setChildRows] = useState<Map<string, IAddressBookData[]>>(() => new Map());
    const childRowsRef = useRef(childRows);
    childRowsRef.current = childRows;
    const [loadingRows, setLoadingRows] = useState<string[]>([]);
    const pendingChildrenRef = useRef(new Set<string>());
    /** Родитель узла — цепочка предков для анти-цикловой фильтрации детей. */
    const parentsRef = useRef(new Map<string, string>());
    /** Узлы, чей ключ входит в стартовый выбор: их id уходят в выделение. */
    const initialSelectionRef = useRef(new Set<string>());
    /** Выбранные в поле сотрудники, чьи узлы ещё не встретились среди загруженных:
     *  выбором таблицы их не представить (строку адресует id узла), а терять при
     *  сабмите нельзя — «Выбрать» перезаписывает поле тем, что доложено отсюда. */
    const [unseenItems, setUnseenItems] = useState<IAddressBookData[]>([]);
    const unseenItemsRef = useRef(unseenItems);
    unseenItemsRef.current = unseenItems;
    /** Последний выбор из таблицы — чтобы доложить актуальное при смене unseen. */
    const lastPickedRef = useRef<IAddressBookData[]>([]);

    /** Доложить наружу полный выбор: строки таблицы + непредставимые в ней. */
    const report = useCallback((picked: IAddressBookData[], unseen: IAddressBookData[]) => {
        const s = settingsRef.current;
        // одиночный выбор: клик по строке заменяет прежнее значение целиком
        const rest =
            !s.isMultiple && picked.length
                ? []
                : unseen.filter((item) => !picked.some((r) => String(r.key) === String(item.key)));
        const all = [...picked, ...rest];
        s.onSelectedKeys(all.map((row) => Number(row.key)));
        s.onSelectedRowItems(all);
    }, []);

    useImperativeHandle(p.controlRef, () => ({
        reset: () => {
            setReloadToken((token) => token + 1);
        },
        unSelect: (item: IAddressBookData) => {
            // сотрудник из незагруженной ветки живёт вне выбора таблицы
            if (unseenItemsRef.current.some((unseen) => String(unseen.key) === String(item.key))) {
                const next = unseenItemsRef.current.filter((unseen) => String(unseen.key) !== String(item.key));
                unseenItemsRef.current = next;
                setUnseenItems(next);
                report(lastPickedRef.current, next);
                return;
            }
            const { keys, rows } = tableRef.current?.getSelected() ?? { keys: [], rows: [] };
            const dropped = rows.filter((row) => String(row.key) === String(item.key)).map((row) => row.id);
            tableRef.current?.selectRows(keys.filter((key) => !dropped.includes(key)));
        },
        clearSelection: () => {
            unseenItemsRef.current = [];
            lastPickedRef.current = [];
            setUnseenItems([]);
            tableRef.current?.clearSelection();
            settingsRef.current.onSelectedKeys([]);
            settingsRef.current.onSelectedRowItems([]);
        },
    }));

    // Новая выборка (смена вкладки или обновление) — прежние ветки не её.
    // Значение поля при этом остаётся значением поля: выбранные сотрудники
    // подтягиваются данными и докладываются как есть, пока их узлы не загружены.
    useEffect(() => {
        setChildRows(new Map());
        setLoadingRows([]);
        pendingChildrenRef.current.clear();
        initialSelectionRef.current.clear();
        lastPickedRef.current = [];
        const selected = settingsRef.current.selected ?? [];
        if (!selected.length) {
            setUnseenItems([]);
            return;
        }
        let alive = true;
        AddressBookService.fetchAbookItemsByKeys(selected.map(String))
            .then((response) => {
                if (!alive) return;
                const items = response.data ?? [];
                setUnseenItems(items);
                report(lastPickedRef.current, items);
            })
            .catch((error: unknown) => {
                if (!alive) return;
                sendNotification({
                    message: `Выбранные ранее записи не загрузились. ${errorText(error, 'Они сохранятся, если закрыть окно кнопкой «Отменить».')}`,
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
            });
        return () => {
            alive = false;
        };
        // report стабилен
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [p.currentTab, reloadToken]);

    /** Отметить узлы, чьи сотрудники уже выбраны в поле.
     *  Значение поля — числовые ключи, а бэкенд отдаёт `key` строкой: сравниваем строками. */
    const markSelectedNodes = useCallback((rows: IAddressBookData[]) => {
        const selected = settingsRef.current.selected ?? [];
        if (selected.length === 0) return;
        const selectedKeys = new Set(selected.map(String));
        const found = rows.filter((row) => selectedKeys.has(String(row.key)));
        if (found.length === 0) return;
        found.forEach((row) => initialSelectionRef.current.add(row.id));
        // добавляем к текущему выбору, не перетирая клики, сделанные до дозагрузки
        const current = tableRef.current?.getSelected().keys ?? [];
        tableRef.current?.selectRows([...new Set([...current, ...initialSelectionRef.current])]);
        // узел загружен — сотрудником теперь распоряжается выбор таблицы
        const foundKeys = new Set(found.map((row) => String(row.key)));
        setUnseenItems((prev) =>
            prev.some((item) => foundKeys.has(String(item.key)))
                ? prev.filter((item) => !foundKeys.has(String(item.key)))
                : prev,
        );
    }, []);

    // вкладка задаёт кэш выборки: её смена — новый загрузчик, т. е. другая выборка
    // (иначе таблица осталась бы на узлах прежней вкладки)
    const tabCache = p.currentTab.cache;

    const fetchNodes = useCallback(
        async (options: IServerLoadOptions, node: string) => {
            const s = settingsRef.current;
            const response = await AddressBookService.fetchTreeData(tabCache, node, s.selected, s.getParams(options));
            const rows = response.data.data ?? [];
            return { rows, totalCount: response.data.totalCount ?? rows.length };
        },
        [tabCache],
    );

    const loadRoots = useCallback(
        async (options: IServerLoadOptions) => {
            const result = await fetchNodes(options, ROOT_NODE);
            markSelectedNodes(result.rows);
            return result;
        },
        [fetchNodes, markSelectedNodes],
    );

    const source = useMemo<IDataTableSource<IAddressBookData>>(
        () => ({ load: loadRoots, refreshToken: reloadToken }),
        [loadRoots, reloadToken],
    );

    const expandRow = useCallback(
        async (row: IAddressBookData) => {
            if (childRowsRef.current.has(row.id) || pendingChildrenRef.current.has(row.id)) return;
            pendingChildrenRef.current.add(row.id);
            setLoadingRows([...pendingChildrenRef.current]);
            try {
                const { rows: loaded } = await fetchNodes({ skip: 0, take: 0, requireTotalCount: true }, row.id);
                // Анти-цикл: узел не может быть собственным ребёнком или ребёнком
                // своего потомка — кривой parent в данных уводил построение дерева
                // в бесконечную рекурсию
                const ancestors = new Set<string>();
                for (let cur: string | undefined = row.id, i = 0; cur && i < 200; i += 1) {
                    ancestors.add(cur);
                    cur = parentsRef.current.get(cur);
                }
                const rows = loaded.filter((child) => !ancestors.has(child.id));
                rows.forEach((child) => parentsRef.current.set(child.id, row.id));
                setChildRows((prev) => new Map(prev).set(row.id, rows));
                markSelectedNodes(rows);
            } catch (error: unknown) {
                // ветка осталась свёрнутой: дерево цело, потеряно только раскрытие
                sendNotification({
                    message: `Вложенные элементы не загрузились. ${errorText(error, 'Повторите раскрытие.')}`,
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
            } finally {
                pendingChildrenRef.current.delete(row.id);
                setLoadingRows([...pendingChildrenRef.current]);
            }
        },
        [fetchNodes, markSelectedNodes],
    );

    const getSubRows = useCallback((row: IAddressBookData) => childRows.get(row.id), [childRows]);
    const canExpandRow = useCallback((row: IAddressBookData) => !!row.hasItems, []);
    const isRowSelectable = useCallback((row: IAddressBookData) => !!row.canBeSelected, []);

    // фильтровать в дереве разрешено только по наименованию
    const columns = useMemo(
        () =>
            buildAbookColumns(p.currentTab.columns, {
                filterableOnly: (column) => column.name === 'Наименование',
            }),
        [p.currentTab.columns],
    );

    const recursive = !!p.isMultiple && (p.recursive ?? false);

    const onSelectionChange = useCallback(
        (_keys: string[], rows: IAddressBookData[]) => {
            // при рекурсивном выборе в поле уходят конечные элементы: отмеченная
            // ветка — только способ выбрать всех её сотрудников
            const picked = recursive ? rows.filter((row) => !row.hasItems) : rows;
            lastPickedRef.current = picked;
            report(picked, unseenItemsRef.current);
        },
        [recursive, report],
    );

    return (
        <DataTable<IAddressBookData>
            controlRef={tableRef}
            columns={columns}
            data={[]}
            getRowId={(row) => row.id}
            source={source}
            getSubRows={getSubRows}
            canExpandRow={canExpandRow}
            onExpandRow={expandRow}
            loadingRowIds={loadingRows}
            // список идёт сплошным: строки догружаются прокруткой, а под таблицей
            // висит счётчик «Загружено X из Y»
            serverScrollMode="infinite"
            paging={false}
            selectable
            singleSelection={!p.isMultiple}
            selectOnRowClick
            isRowSelectable={isRowSelectable}
            recursiveSelection={recursive}
            onSelectionChange={onSelectionChange}
            onRowDoubleClick={p.isMultiple ? undefined : p.onConfirmRow}
            // строка фильтров в дереве по умолчанию скрыта,
            // но её можно открыть воронкой в тулбаре
            filterRowToggle
            applyFiltersOnClick={applyFilterOnClick}
            defaultWrapMode={p.wordWrapEnabled ? 'wrap' : 'nowrap'}
            // высота — от зоны .abook-grid, см. ListModeControl
            fillHeight
            fitColumns
            overflowTooltip
            emptyText="Нет строк"
        />
    );
};

export default TreeModeControl;
